/**
 * Storage tab — compact per-tenant database size breakdown
 *
 * Shows every tenant sorted by DB size (biggest first) with a mini bar
 * that visualises its share of the platform's total footprint, plus a
 * summary row (grand total, average, biggest tenant). The data is
 * already carried by the tenants list (`db_size_mb` + `counts`),
 * so this tab does not issue any extra request.
 */
import { useMemo } from "react";
import { HardDrive, Database, ArrowDownAZ, Boxes } from "lucide-react";
import { fmtDate } from "@/lib/supervisorFormat";

const fmtMB = (v) => (Number.isFinite(v) ? v.toFixed(2) : "0.00");

export default function StorageTab({ tenants }) {
  const { rows, totalMB, biggest, avgMB } = useMemo(() => {
    const list = (tenants || []).map((t) => ({
      code: t.tenant_code,
      name: t.business_name || "",
      sizeMB: Number(t.db_size_mb ?? 0),
      users: t.counts?.users ?? 0,
      products: t.counts?.products ?? 0,
      tables: t.counts?.tables ?? 0,
      receipts: t.counts?.receipt_logs ?? 0,
      lastLogin: t.last_login_at,
      suspended: !!t.suspended,
    }));
    list.sort((a, b) => b.sizeMB - a.sizeMB);
    const total = list.reduce((s, r) => s + r.sizeMB, 0);
    const big = list[0]?.sizeMB || 0;
    return {
      rows: list,
      totalMB: total,
      biggest: big,
      avgMB: list.length ? total / list.length : 0,
    };
  }, [tenants]);

  return (
    <div className="space-y-4" data-testid="sup-storage-tab">
      {/* Summary cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <SummaryCard
          icon={Database}
          label="Σύνολο σε όλες τις βάσεις"
          value={`${fmtMB(totalMB)} MB`}
          color="amber"
          testid="storage-total-mb"
        />
        <SummaryCard
          icon={HardDrive}
          label="Μεγαλύτερος πελάτης"
          value={`${fmtMB(biggest)} MB`}
          color="rose"
        />
        <SummaryCard
          icon={ArrowDownAZ}
          label="Μέσος όρος"
          value={`${fmtMB(avgMB)} MB`}
          color="emerald"
        />
        <SummaryCard
          icon={Boxes}
          label="Πλήθος βάσεων"
          value={rows.length}
          color="blue"
        />
      </div>

      {/* Per-tenant breakdown */}
      <div className="rounded-xl border border-zinc-800 bg-zinc-900/50 overflow-hidden">
        <div className="px-4 py-3 border-b border-zinc-800 flex items-center gap-2">
          <HardDrive size={14} className="text-amber-400" />
          <span className="text-zinc-300 text-sm font-bold">
            Αναλυτική κατανομή ανά βάση
          </span>
          <span className="text-zinc-500 text-xs">
            (ταξινομημένο κατά μέγεθος)
          </span>
        </div>
        <table className="w-full text-sm">
          <thead className="bg-zinc-900 text-zinc-400 text-[11px] uppercase tracking-wider">
            <tr>
              <th className="px-4 py-2 text-left">Κωδικός</th>
              <th className="px-4 py-2 text-left">Επιχείρηση</th>
              <th className="px-4 py-2 text-right">Μέγεθος</th>
              <th className="px-4 py-2 text-left w-1/3">% του συνόλου</th>
              <th className="px-4 py-2 text-right">Χρήστες</th>
              <th className="px-4 py-2 text-right">Προϊόντα</th>
              <th className="px-4 py-2 text-right">Τραπέζια</th>
              <th className="px-4 py-2 text-right">Αποδείξεις</th>
              <th className="px-4 py-2 text-left">Τελ. Σύνδεση</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && (
              <tr>
                <td colSpan={9} className="px-4 py-10 text-center text-zinc-500">
                  Δεν υπάρχουν πελάτες
                </td>
              </tr>
            )}
            {rows.map((r) => {
              const pct = totalMB > 0 ? (r.sizeMB / totalMB) * 100 : 0;
              return (
                <tr
                  key={r.code}
                  className={`border-t border-zinc-800 hover:bg-zinc-900/70 ${
                    r.suspended ? "opacity-60" : ""
                  }`}
                  data-testid={`storage-row-${r.code}`}
                >
                  <td className="px-4 py-2 font-mono font-bold text-amber-400">
                    {r.code}
                  </td>
                  <td className="px-4 py-2 text-zinc-200">{r.name}</td>
                  <td className="px-4 py-2 text-right font-mono font-bold text-zinc-100">
                    {fmtMB(r.sizeMB)} MB
                  </td>
                  <td className="px-4 py-2">
                    <div className="flex items-center gap-2">
                      <div className="flex-1 h-2 bg-zinc-800 rounded overflow-hidden">
                        <div
                          className="h-full bg-gradient-to-r from-amber-500 to-rose-500"
                          style={{ width: `${Math.min(100, pct)}%` }}
                        />
                      </div>
                      <span className="text-xs font-mono text-zinc-400 w-12 text-right">
                        {pct.toFixed(1)}%
                      </span>
                    </div>
                  </td>
                  <td className="px-4 py-2 text-right text-zinc-400 font-mono text-xs">
                    {r.users}
                  </td>
                  <td className="px-4 py-2 text-right text-zinc-400 font-mono text-xs">
                    {r.products}
                  </td>
                  <td className="px-4 py-2 text-right text-zinc-400 font-mono text-xs">
                    {r.tables}
                  </td>
                  <td className="px-4 py-2 text-right text-zinc-400 font-mono text-xs">
                    {r.receipts}
                  </td>
                  <td className="px-4 py-2 text-xs text-zinc-500">
                    {fmtDate(r.lastLogin)}
                  </td>
                </tr>
              );
            })}
          </tbody>
          {rows.length > 0 && (
            <tfoot>
              <tr className="border-t-2 border-zinc-700 bg-zinc-900/80">
                <td className="px-4 py-3 font-bold text-zinc-300" colSpan={2}>
                  ΣΥΝΟΛΟ
                </td>
                <td
                  className="px-4 py-3 text-right font-mono font-bold text-amber-400"
                  data-testid="storage-total-mb-cell"
                >
                  {fmtMB(totalMB)} MB
                </td>
                <td className="px-4 py-3 text-zinc-500 text-xs">100%</td>
                <td className="px-4 py-3 text-right text-zinc-400 font-mono text-xs">
                  {rows.reduce((s, r) => s + r.users, 0)}
                </td>
                <td className="px-4 py-3 text-right text-zinc-400 font-mono text-xs">
                  {rows.reduce((s, r) => s + r.products, 0)}
                </td>
                <td className="px-4 py-3 text-right text-zinc-400 font-mono text-xs">
                  {rows.reduce((s, r) => s + r.tables, 0)}
                </td>
                <td className="px-4 py-3 text-right text-zinc-400 font-mono text-xs">
                  {rows.reduce((s, r) => s + r.receipts, 0)}
                </td>
                <td />
              </tr>
            </tfoot>
          )}
        </table>
      </div>
    </div>
  );
}

function SummaryCard({ icon: Ico, label, value, color, testid }) {
  const colors = {
    amber: "from-amber-600 to-amber-500 text-amber-100",
    emerald: "from-emerald-600 to-emerald-500 text-emerald-100",
    rose: "from-rose-600 to-rose-500 text-rose-100",
    blue: "from-blue-600 to-blue-500 text-blue-100",
  };
  return (
    <div
      className="rounded-xl border border-zinc-800 bg-zinc-900/50 p-4"
      data-testid={testid}
    >
      <div
        className={`w-9 h-9 rounded-lg bg-gradient-to-br ${colors[color]} grid place-items-center mb-2`}
      >
        <Ico size={16} />
      </div>
      <div className="text-2xl font-bold font-mono text-zinc-100">{value}</div>
      <div className="text-[11px] text-zinc-500 mt-0.5 uppercase tracking-wider font-bold">
        {label}
      </div>
    </div>
  );
}
